import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { Table, Button, Icon, Label } from "semantic-ui-react";
import { toast, ToastContainer } from "react-toastify";
import { LoadingSpinner, BasicModal, ConfirmDialog } from "../../../Shared";
import { Employee } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { EmployeeForm } from "../EmployeeForm";
import { EmployeeImportModal } from "../EmployeeImportModal";
import searchSvg from "../../../../assets/svg/search.svg";
import "./ListEmployees.scss";

const empController = new Employee();

export function ListEmployees({ active: activeFilter, reload, onReload }) {
  const { accessToken } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const [items,       setItems]       = useState(null);
  const [itemsFilter, setItemsFilter] = useState([]);
  const [search,      setSearch]      = useState("");
  const [tipoFilter,  setTipoFilter]  = useState("");
  const [showModal,   setShowModal]   = useState(false);
  const [showImport,  setShowImport]  = useState(false);
  const [selected,    setSelected]    = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmMsg,  setConfirmMsg]  = useState("");
  const [isDelete,    setIsDelete]    = useState(false);
  const [rowMenu,     setRowMenu]     = useState(null); // { id, top, left, openUp } | null

  useEffect(() => {
    if (!rowMenu) return;
    const handler = (e) => {
      if (!e.target.closest(".employees-list__row-menu, .employees-list__row-menu-list")) setRowMenu(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [rowMenu]);

  const toggleRowMenu = (id, e) => {
    if (rowMenu?.id === id) { setRowMenu(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuHeight = 150;
    const openUp = rect.bottom + menuHeight > window.innerHeight;
    setRowMenu({
      id,
      left: rect.right - 170,
      top: openUp ? rect.top - 4 : rect.bottom + 4,
      openUp,
    });
  };

  useEffect(() => {
    (async () => {
      try {
        setItems(null);
        const filters = activeFilter !== undefined ? { active: activeFilter } : {};
        const res = await empController.getEmployees(accessToken, filters);
        setItems(res);
        setItemsFilter(res);
      } catch {
        setItems([]);
        setItemsFilter([]);
      }
    })();
  }, [activeFilter, reload]);

  useEffect(() => {
    if (!items) return;
    const q = search.toLowerCase();
    setItemsFilter(
      items.filter((i) => {
        const matchText = !q ||
          i.nombre?.toLowerCase().includes(q) ||
          i.apellido?.toLowerCase().includes(q) ||
          i.numero_legajo?.toLowerCase().includes(q);
        const matchTipo = !tipoFilter || i.tipo === tipoFilter;
        return matchText && matchTipo;
      })
    );
  }, [search, tipoFilter, items]);

  const openCreate = () => { setSelected(null); setShowModal(true); };
  const openEdit   = (i) => { setSelected(i);   setShowModal(true); };

  const openToggle = (i) => {
    setSelected(i); setIsDelete(false);
    setConfirmMsg(`¿${i.active ? t("deactivate") : t("activate")} "${i.nombre} ${i.apellido}"?`);
    setShowConfirm(true);
  };

  const openDelete = (i) => {
    setSelected(i); setIsDelete(true);
    setConfirmMsg(`¿${t("delete")} "${i.nombre} ${i.apellido}"?`);
    setShowConfirm(true);
  };

  const handleToggle = async () => {
    try {
      await empController.updateEmployee(accessToken, selected._id, { active: !selected.active });
      toast.success(selected.active ? t("deactivated") : t("activated"));
      setShowConfirm(false); onReload();
    } catch { toast.error(t("error")); }
  };

  const handleDelete = async () => {
    try {
      await empController.deleteEmployee(accessToken, selected._id);
      toast.success(t("deleted"));
      setShowConfirm(false); onReload();
    } catch (err) { toast.error(err?.msg || t("error")); }
  };

  if (items === null) return <LoadingSpinner />;

  return (
    <div className="employees-list">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="employees-list__card">
        <div className="employees-list__filters">
          <div className="employees-list__filter-block employees-list__filter-search">
            <div className="search-user-wrapper">
              <img src={searchSvg} alt="search" className="search-user-icon" />
              <input
                className="search-user-input"
                placeholder="Buscar..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <FilterDropdown
              placeholder="Tipo"
              options={[
                { key: "permanente", value: "permanente", text: "Permanente" },
                { key: "eventual",   value: "eventual",   text: "Eventual" },
              ]}
              value={tipoFilter}
              onChange={setTipoFilter}
            />
          </div>
          <div className="employees-list__filter-actions">
            <Button icon className="add-employees-btn add-employees-btn--import" title={t("import_employees")} onClick={() => setShowImport(true)}>
              <Icon name="upload" />
            </Button>
            <Button icon className="add-employees-btn" title={t("new_employee")} onClick={openCreate}>
              <Icon name="plus" />
            </Button>
          </div>
        </div>

        {itemsFilter.length === 0 ? (
          <div className="list-empty-state">
                <Icon name="folder open outline" className="list-empty-state__icon" />
                <p className="list-empty-state__text">{t("no_results")}</p>
              </div>
        ) : (
          <div className="employees-list__tbl-scroll">
            <Table className="employees-table unstackable rtable-cards row-hover">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>#</Table.HeaderCell>
                  <Table.HeaderCell>{t("record_number")}</Table.HeaderCell>
                  <Table.HeaderCell>{t("first_name")}</Table.HeaderCell>
                  <Table.HeaderCell>{t("last_name")}</Table.HeaderCell>
                  <Table.HeaderCell>Tipo</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right"></Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {itemsFilter.map((i, idx) => (
                  <Table.Row key={i._id}>
                    <Table.Cell data-label="#">{idx + 1}</Table.Cell>
                    <Table.Cell data-label={t("record_number")}>{i.numero_legajo || "-"}</Table.Cell>
                    <Table.Cell data-label={t("first_name")}>{i.nombre || "-"}</Table.Cell>
                    <Table.Cell data-label={t("last_name")}>{i.apellido || "-"}</Table.Cell>
                    <Table.Cell data-label="Tipo">
                      <Label size="tiny" color={i.tipo === "eventual" ? "orange" : "blue"}>
                        {i.tipo === "eventual" ? "Eventual" : "Permanente"}
                      </Label>
                    </Table.Cell>
                    <Table.Cell textAlign="center" data-label="" className="employees-table__actions" onClick={(e) => e.stopPropagation()}>
                      <div className="employees-list__row-menu">
                        <button type="button" className="employees-list__row-menu-btn" onClick={(e) => toggleRowMenu(i._id, e)} aria-label={t("actions")}>
                          <Icon name="ellipsis vertical" />
                        </button>
                      </div>
                      {rowMenu?.id === i._id && ReactDOM.createPortal(
                        <div
                          className="employees-list__row-menu-list"
                          style={{
                            position: "fixed",
                            top: rowMenu.top,
                            left: rowMenu.left,
                            transform: rowMenu.openUp ? "translateY(-100%)" : "none",
                          }}
                        >
                          <button onClick={() => { openEdit(i); setRowMenu(null); }}>
                            <Icon name="pencil" />{t("edit")}
                          </button>
                          <button onClick={() => { openToggle(i); setRowMenu(null); }}>
                            <Icon name={i.active ? "ban" : "check"} />{i.active ? t("deactivate") : t("activate")}
                          </button>
                          <button className="employees-list__row-menu-item--danger" onClick={() => { openDelete(i); setRowMenu(null); }}>
                            <Icon name="trash" />{t("delete")}
                          </button>
                        </div>,
                        document.body
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </div>

      <BasicModal
        show={showModal}
        close={() => setShowModal(false)}
        title={selected ? `${t("edit")}: ${selected.nombre} ${selected.apellido}` : t("new_employee")}
        size="small"
      >
        <EmployeeForm employee={selected} close={() => setShowModal(false)} onReload={onReload} />
      </BasicModal>

      <BasicModal
        show={showImport}
        close={() => setShowImport(false)}
        title="Importar empleados"
        size="large"
      >
        <EmployeeImportModal onClose={() => setShowImport(false)} onReload={onReload} />
      </BasicModal>

      <ConfirmDialog
        open={showConfirm}
        title={isDelete ? "Eliminar" : (selected?.active ? "Desactivar" : "Activar")}
        message={confirmMsg}
        onCancel={() => setShowConfirm(false)}
        onConfirm={isDelete ? handleDelete : handleToggle}
        confirmLabel="Aceptar"
        cancelLabel={t("cancel")}
        confirmDanger={isDelete}
      />
    </div>
  );
}

function FilterDropdown({ options, value, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const selected = options.find((o) => o.value === value);

  return (
    <div className="employees-list__fd" ref={ref}>
      <button type="button" className="employees-list__fd-toggle" onClick={() => setOpen((v) => !v)}>
        <span className={selected ? "" : "employees-list__fd-placeholder"}>{selected ? selected.text : placeholder}</span>
        <span className="employees-list__fd-icons">
          {selected && (
            <span
              className="employees-list__fd-clear"
              onClick={(e) => { e.stopPropagation(); onChange(""); setOpen(false); }}
              title={placeholder}
            >
              ✕
            </span>
          )}
          <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width="12" height="12" className={`employees-list__fd-arrow${open ? " employees-list__fd-arrow--open" : ""}`}>
            <path d="M6 1.5v9M2.5 7 6 10.5 9.5 7"/>
          </svg>
        </span>
      </button>
      {open && (
        <div className="employees-list__fd-menu">
          {options.map((o) => (
            <button
              type="button"
              key={o.key}
              className={`employees-list__fd-item${o.value === value ? " employees-list__fd-item--active" : ""}`}
              onClick={() => { onChange(o.value); setOpen(false); }}
            >
              {o.text}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
